import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect, useRef, useState,
} from 'react';

import { Icon, ScrollIndicator } from '@powdr/components';
import { useIsInView } from '@powdr/hooks';

import {
  StyledVerticalTableMarquee, MarqueeTable,
  HeaderCell, StatCell, NameCell, Row,
  IndexText, Name,
  ImageWrapper, Image, ImagePlaceholder,
  TableTitle,
  TableHeader, TableBody,
  HeaderWrapper,
  ScrollableContainer,
  EmptyBoardMessage,
  IconCell,
} from './styles';

export const VerticalTableMarquee = ({
  className = '',
  marqueeTitle,
  hideHeader = false,
  disableAutoScroll = false,
  disableAlternatingColors = false,
  headerColorOverrides = null,
  leftColumnTitle,
  rightColumnTitle,
  showPhotos = false,
  showItemIndex = false,
  isWrapperless = false,
  enableUserScrolling = false,
  parentColorProfile,
  emptyMessage = null,
  is5050 = false,
  data,
}) => {
  const scrollRef = useRef(null);
  const isBodyInView = useIsInView(scrollRef);
  const [fading, setFading] = useState(false);
  const [hasAnimationStarted, setHasAnimationStarted] = useState(false);
  const [hasUserScrolled, setHasUserScrolled] = useState(!enableUserScrolling);
  const scrollDuration = (data.length - 10) * 1250; // milliseconds
  const fadeDuration = 1000; // milliseconds
  const pauseBeforeRestart = 3000; // milliseconds
  const startDelay = 3000; // milliseconds

  useEffect(() => {
    if (
      !data?.length
      || !scrollRef.current
      || enableUserScrolling
      || disableAutoScroll
    ) return;

    // Start animation only if in view and not started yet
    if (isBodyInView || !hasAnimationStarted.current) {
      if (isBodyInView && !hasAnimationStarted.current) {
        setHasAnimationStarted(true);
      }

      const el = scrollRef.current;
      let animationFrameId;
      let fadeTimeoutId;
      let pauseTimeoutId;
      let startDelayTimeoutId;
      let animationStartTime = null;

      const maxScroll = el.scrollHeight - el.clientHeight;

      const step = (timestamp) => {
        if (!animationStartTime) animationStartTime = timestamp;
        const elapsed = timestamp - animationStartTime;
        const progress = Math.min(elapsed / scrollDuration, 1);
        el.scrollTop = maxScroll * progress;

        if (progress < 1) {
          animationFrameId = requestAnimationFrame(step);
        } else {
          pauseTimeoutId = setTimeout(() => {
            setFading(true);

            fadeTimeoutId = setTimeout(() => {
              el.scrollTop = 0;
              setFading(false);
              animationStartTime = null;

              startDelayTimeoutId = setTimeout(() => {
                animationFrameId = requestAnimationFrame(step);
              }, startDelay);
            }, fadeDuration);
          }, pauseBeforeRestart);
        }
      };

      // Start animation after delay
      startDelayTimeoutId = setTimeout(() => {
        animationFrameId = requestAnimationFrame(step);
      }, startDelay);

      // eslint-disable-next-line consistent-return
      return () => {
        cancelAnimationFrame(animationFrameId);
        clearTimeout(fadeTimeoutId);
        clearTimeout(pauseTimeoutId);
        clearTimeout(startDelayTimeoutId);
      };
    }
  }, [
    data,
    enableUserScrolling,
    isBodyInView,
    hasAnimationStarted,
    scrollDuration,
    disableAutoScroll,
  ]);

  const handlePointerDown = (e) => {
    if (scrollRef.current?.contains(e.target)) {
      scrollRef.current = true;
    }
  };

  const handleScroll = useCallback(() => {
    if (scrollRef.current && !hasUserScrolled) {
      setHasUserScrolled(true);
      scrollRef.current = false;
    }
  }, [hasUserScrolled]);

  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    scrollEl.addEventListener('scroll', handleScroll);

    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      scrollEl.removeEventListener('scroll', handleScroll);
    };
  }, [hasUserScrolled, handleScroll]);

  return (
    <StyledVerticalTableMarquee
      className={className}
      $isWrapperless={isWrapperless}
      $is5050={is5050}
    >
      <MarqueeTable role="table">
        {/* Marquee Header */}
        {(!hideHeader) && (
          <HeaderWrapper $adjustSizing={disableAutoScroll}>
            {(marqueeTitle && marqueeTitle !== '') && (
              <TableTitle
                $isWrapperless={isWrapperless}
                $colorProfile={parentColorProfile}
              >
                {marqueeTitle}
              </TableTitle>
            )}
            <TableHeader role="rowgroup" $colorProfile={parentColorProfile}>
              <Row
                role="row"
                $adjustSizing={disableAutoScroll}
                $numItems={data.length + 1} // add 1 for header row
              >
                <HeaderCell
                  role="columnheader"
                  className="cell"
                  $isWrapperless={isWrapperless}
                  $colorProfile={parentColorProfile}
                  $headerColorOverrides={headerColorOverrides}
                >
                  {leftColumnTitle}
                </HeaderCell>
                <HeaderCell
                  role="columnheader"
                  className="stat-header cell"
                  $isWrapperless={isWrapperless}
                  $alignRight
                  $colorProfile={parentColorProfile}
                  $headerColorOverrides={headerColorOverrides}
                >
                  {rightColumnTitle}
                </HeaderCell>
              </Row>
            </TableHeader>
          </HeaderWrapper>
        )}

        {/* Scrollable Table Body */}
        <ScrollableContainer
          ref={scrollRef}
          $isWrapperless={isWrapperless}
          $fading={fading}
          $fadeDuration={fadeDuration}
          $disableScroll={!enableUserScrolling}
          onScroll={handleScroll}
        >
          <TableBody role="rowgroup">
            {(data?.length > 0) ? data?.map((entry, idx) => (
              <Row
                key={entry.guid}
                role="row"
                $isWrapperless={isWrapperless}
                $disableAlternatingColors={disableAlternatingColors}
                $adjustSizing={disableAutoScroll}
                $numItems={data.length + 1} // add 1 for header row
              >
                <NameCell
                  className="cell"
                  role="cell"
                  $isWrapperless={isWrapperless}
                >
                  {/* Item Index */}
                  {(showItemIndex) && <IndexText $isWrapperless={isWrapperless}>{`${idx + 1}.`}</IndexText>}

                  {/* Photos */}
                  {(showPhotos) && (
                    <ImageWrapper
                      $colorProfile={parentColorProfile}
                      $isWrapperless={isWrapperless}
                    >
                      {entry?.photoSrcUrl ? (
                        <Image src={entry.photoSrcUrl} />
                      ) : (
                        <ImagePlaceholder
                          $colorProfile={parentColorProfile}
                          $isWrapperless={isWrapperless}
                        >
                          {entry.placeholderText}
                        </ImagePlaceholder>
                      )}
                    </ImageWrapper>
                  )}

                  {/* Item Name */}
                  <Name
                    $isWrapperless={isWrapperless}
                  >
                    {entry.name}
                  </Name>
                </NameCell>

                {(entry?.showIcon) ? (
                  <IconCell
                    className="cell"
                    role="cell"
                    $isWrapperless={isWrapperless}
                  >
                    <Icon
                      className="cell-icon"
                      name={entry.icon}
                    />
                  </IconCell>
                ) : (
                  <StatCell
                    className="cell"
                    role="cell"
                    $isWrapperless={isWrapperless}
                    $isBold={entry.boldStat}
                    $colorOverride={entry.textColorOverride}
                  >
                    {console.log('entry', entry)}
                    {entry.stat}
                  </StatCell>
                )}
              </Row>
            )) : (
              <EmptyBoardMessage $isWrapperless={isWrapperless}>
                {emptyMessage}
              </EmptyBoardMessage>
            )}
          </TableBody>
        </ScrollableContainer>

        {/* Scroll Indicator */}
        {enableUserScrolling && (
          <ScrollIndicator
            parentColorProfile={parentColorProfile}
            bottomOffset={15}
            loopAnimation
            fadeOut={hasUserScrolled}
          />
        )}
      </MarqueeTable>
    </StyledVerticalTableMarquee>
  );
};

VerticalTableMarquee.propTypes = {
  className: PropTypes.string,
  marqueeTitle: PropTypes.string,
  parentColorProfile: PropTypes.string,
  isWrapperless: PropTypes.bool,
  enableUserScrolling: PropTypes.bool,
  leftColumnTitle: PropTypes.string.isRequired,
  rightColumnTitle: PropTypes.string.isRequired,
  showPhotos: PropTypes.bool,
  showItemIndex: PropTypes.bool,
  is5050: PropTypes.bool,
  emptyMessage: PropTypes.string,
  hideHeader: PropTypes.bool,
  disableAutoScroll: PropTypes.bool,
  disableAlternatingColors: PropTypes.bool,
  headerColorOverrides: PropTypes.shape({
    text: PropTypes.string,
    background: PropTypes.string,
  }),
  data: PropTypes.arrayOf(PropTypes.shape({})),
};
